import { gql, useQuery } from "@apollo/client";
import React from "react";
import { Link } from "react-router-dom";
import { Graph } from "src/generated/graph";
import ReactTooltip from "react-tooltip";
import { Layout, LegacyCard, SkeletonBodyText } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { PricesToolTips } from "src/screens/applications/products/item/upload/PricesToolTips";

const QUERY_DASHBOARD = gql`
  query sellerSalesSummaryDashboard {
    sellerSalesSummaryDashboard
  }
`;

function CalculateProgress(data: { sold: number; ongoing: number; returned: number; lost: number }) {
  const max = Math.max(data.sold, data.ongoing, data.returned, data.lost);
  let decreament = 0;
  let str = "";

  for (const k of Object.keys(data)) {
    if (Number((data as any)[k]) < 3 && Number((data as any)[k]) > 0) {
      decreament = decreament + ((data as any)[k] + 3);
      (data as any)[k] = (data as any)[k] + 3;
    }

    if (Number((data as any)[k]) === max) {
      str = k;
    }
  }

  return {
    sold: str === "sold" ? max - decreament : data.sold,
    ongoing: str === "onging" ? max - decreament : data.ongoing,
    returned: str === "returned" ? max - decreament : data.returned,
    lost: str === "lost" ? max - decreament : data.lost,
    max: str,
  };
}

function SaleSummaryV2(): React.ReactElement {
  const { t } = useTranslation();
  const { data } = useQuery<Graph.Query>(QUERY_DASHBOARD, {
    fetchPolicy: "no-cache",
  });

  return (
    <Layout.Section variant="oneHalf">
      <LegacyCard
        title={t("page_dashboard.sale_summary.title")}
        actions={[{ content: t("general.more_detail"), url: "/dashboard/sale/day", external: true }]}
      >
        <LegacyCard.Section>
          {!data ? (
            <SkeletonBodyText />
          ) : (
            <div>
              {Object.keys(data.sellerSalesSummaryDashboard).map((key, idx) => {
                const keys = Object.keys(data.sellerSalesSummaryDashboard[key].orderAmount);

                const total = keys
                  .map((k) => data.sellerSalesSummaryDashboard[key].orderQty[k])
                  .reduce((s, c) => s + c, 0);

                const totalOrder = keys
                  .map((k) => data.sellerSalesSummaryDashboard[key].orderAmount[k])
                  .reduce((s, c) => s + c, 0);

                const perSold = (Number(data.sellerSalesSummaryDashboard[key].orderQty.sold) * 100) / total;

                const perOngoing = (Number(data.sellerSalesSummaryDashboard[key].orderQty.ongoing) * 100) / total;

                const perReturned = (Number(data.sellerSalesSummaryDashboard[key].orderQty.returned) * 100) / total;

                const perLost = (Number(data.sellerSalesSummaryDashboard[key].orderQty.lost) * 100) / total;

                const res = CalculateProgress({
                  sold: perSold,
                  ongoing: perOngoing,
                  lost: perLost,
                  returned: perReturned,
                });

                const lostValue = 100 - (res.ongoing + res.sold + res.returned + res.lost);

                return (
                  <div key={idx}>
                    <ReactTooltip id={`sold${idx}`} effect="solid" type="success">
                      ${data.sellerSalesSummaryDashboard[key].orderAmount.sold}
                    </ReactTooltip>
                    <ReactTooltip id={`ongoing${idx}`} effect="solid" type="warning">
                      ${data.sellerSalesSummaryDashboard[key].orderAmount.ongoing}
                    </ReactTooltip>
                    <ReactTooltip id={`returned${idx}`} effect="solid" type="info">
                      ${data.sellerSalesSummaryDashboard[key].orderAmount.returned}
                    </ReactTooltip>
                    <ReactTooltip id={`lost${idx}`} effect="solid" type="error">
                      ${data.sellerSalesSummaryDashboard[key].orderAmount.lost}
                    </ReactTooltip>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Link
                        to={`/reports/salesummary?day=${
                          key === "today" ? 1 : key === "sevenDays" ? 7 : key === "fifteenDays" ? 15 : 30
                        }&type=ALL`}
                      >
                        <small style={{ textTransform: "capitalize" }} className="bar-labels">
                          {key === "sevenDays"
                            ? `7 ${t("page_dashboard.sale_summary.days")}`
                            : key === "fifteenDays"
                            ? `15 ${t("page_dashboard.sale_summary.days")}`
                            : key === "thirtyDays"
                            ? `30 ${t("page_dashboard.sale_summary.days")}`
                            : t("page_dashboard.sale_summary.today")}
                        </small>
                      </Link>
                      <small className="bar-labels text-right">
                        ${data.sellerSalesSummaryDashboard[key].orderAmount.sold} {t("general.of")} $
                        {totalOrder.toFixed(2)} {t("page_dashboard.sale_summary.sold")}{" "}
                        {
                          <PricesToolTips>
                            <small>
                              <b>(SOP)</b>{" "}
                            </small>
                          </PricesToolTips>
                        }
                      </small>
                    </div>
                    <div className="progress mb-4">
                      <Link
                        data-tip
                        data-for={`sold${idx}`}
                        className="progress-bar bg-success"
                        style={{
                          width: `${res.max === "sold" ? res.sold + lostValue : res.sold}%`,
                          cursor: "pointer",
                        }}
                        to={`/reports/salesummary?day=${
                          key === "today" ? 1 : key === "sevenDays" ? 7 : key === "fifteenDays" ? 15 : 30
                        }&type=SOLD`}
                      >
                        {data.sellerSalesSummaryDashboard[key].orderQty.sold}
                      </Link>
                      <Link
                        data-tip
                        data-for={`ongoing${idx}`}
                        className="progress-bar bg-warning text-muted"
                        style={{
                          width: `${res.max === "sold" ? res.ongoing + lostValue : res.ongoing}%`,
                          cursor: "pointer",
                        }}
                        to={`/reports/salesummary?day=${
                          key === "today" ? 1 : key === "sevenDays" ? 7 : key === "fifteenDays" ? 15 : 30
                        }&type=ONGOING`}
                      >
                        {data.sellerSalesSummaryDashboard[key].orderQty.ongoing}
                      </Link>
                      <Link
                        data-tip
                        data-for={`returned${idx}`}
                        className="progress-bar"
                        style={{
                          width: `${res.returned}%`,
                          cursor: "pointer",
                        }}
                        to={`/reports/salesummary?day=${
                          key === "today" ? 1 : key === "sevenDays" ? 7 : key === "fifteenDays" ? 15 : 30
                        }&type=RETURNED`}
                      >
                        {data.sellerSalesSummaryDashboard[key].orderQty.returned}
                      </Link>
                      <Link
                        data-tip
                        data-for={`lost${idx}`}
                        className="progress-bar bg-danger"
                        style={{
                          width: `${res.lost}%`,
                          cursor: "pointer",
                        }}
                        to={`/reports/salesummary?day=${
                          key === "today" ? 1 : key === "sevenDays" ? 7 : key === "fifteenDays" ? 15 : 30
                        }&type=LOST`}
                      >
                        {data.sellerSalesSummaryDashboard[key].orderQty.lost}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </LegacyCard.Section>
        <LegacyCard.Section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <label className="mr-3">
                <div className="status-pill green mr-1"></div>
                {t("page_dashboard.sale_summary.sold")}
              </label>
              <label className="mr-3">
                <div className="status-pill yellow mr-1"></div>
                {t("page_dashboard.sale_summary.ongoing")}
              </label>
              <label className="mr-3">
                <div className="status-pill primary mr-1" style={{ backgroundColor: "#047bf8" }}></div>
                {t("page_dashboard.sale_summary.returned")}
              </label>
              <label className="mr-3">
                <div className="status-pill red mr-1"></div>
                {t("page_dashboard.sale_summary.lost")}
              </label>
            </div>
            <Link to="/reports/cohort" className="btn btn-sm btn-primary">
              {t("page_dashboard.sale_summary.customer_cohort")}
            </Link>
          </div>
        </LegacyCard.Section>
      </LegacyCard>
    </Layout.Section>
  );
}

export const SaleSummary = SaleSummaryV2;
